import { cn } from '#app/utils/misc.tsx'
import { Link, NavLink } from '@remix-run/react'
import { Icon } from '../ui/icon.tsx'
import { Divider } from '@intersport-digital/react'
import { iseLogoFull } from '#app/routes/_marketing+/logos/logos.ts'

interface IISEV2DrawerProps {
	opened: boolean
	onClose: () => void
	headerComponent?: JSX.Element
	useClickOutside?: boolean
	children?: React.ReactNode
	mask?: boolean
	maskCustomStyle?: React.CSSProperties
	header?: string
	customStyle?: React.CSSProperties
	colorTheme?: 'light' | 'dark'
}

const ISEV2Drawer: React.FC<IISEV2DrawerProps> = ({ onClose, opened }) => {
	return (
		<div
			className={cn('drawer', opened && 'drawer--open')}
			data-testid="Drawer__root"
		>
			<div
				data-testid="Drawer__mask"
				className={cn(
					'drawer-mask drawer-mask--drawer',
					opened ? 'fade-in' : 'fade-out',
				)}
				onClick={onClose}
			></div>
			<div
				className={cn(
					'drawer__content-wrapper drawer__content-wrapper--position-right !w-11/12',
					opened ? 'slide-in' : 'slide-out-right',
				)}
				data-testid="Drawer__content-wrapper"
			>
				<div className="drawer__content">
					<div className="drawer__wrapper-body">
						<div className="drawer__header">
							<div className="drawer__header-top">
								<div className="d-flex align-items-center flex-grow-1">
									<div className="flex-grow-1">
										<Link
											className="text-color-blue"
											data-testid="link-component"
											to="/"
											onClick={onClose}
										>
											<img
												src={iseLogoFull}
												alt="INTERSPORT Logo"
												className="h-7 md:h-7"
											/>
										</Link>
									</div>
								</div>
								<div
									className="drawer__close"
									data-testid="Drawer__close"
									onClick={onClose}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										fill="none"
										className="icon-container icon-exit"
										viewBox="0 0 24 24"
									>
										<path
											fill="currentColor"
											fill-rule="evenodd"
											d="M5.778 17.091L17.09 5.778l1.415 1.414L7.192 18.506 5.778 17.09z"
										></path>
										<path
											fill="currentColor"
											fill-rule="evenodd"
											d="M6.909 5.777l11.314 11.314-1.415 1.414L5.495 7.192l1.414-1.415z"
										></path>
									</svg>
								</div>
							</div>
						</div>
						<div className="drawer__body drawer__body--with-footer uds-scroll uds-scroll--sizeless">
							<div className="mobile-menu">
								<nav aria-label="Mobile navigation" className="mb-8">
									<ul
										className="mobile-nav-list"
										data-testid="LinkList-component"
									>
										<li className="mobile-nav-link">
											<div data-testid="tabLink-component">
												<NavLink
													className="mobile-nav-item"
													data-testid="link-component"
													to="/butiker"
													onClick={onClose}
												>
													<div className="align-center flex flex-row gap-4">
														<Icon name="map-pin" /> Butiker
													</div>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														fill="none"
														className="icon-container icon-next"
														viewBox="0 0 24 24"
													>
														<path
															stroke="currentColor"
															stroke-width="2"
															d="M9 4l5.55 7.4a1 1 0 010 1.2L9 20"
														></path>
													</svg>
												</NavLink>
											</div>
										</li>
										<Divider
											size="thin"
											color="white"
											classes={{ root: 'my-5' }}
										/>
										<li className="mobile-nav-link">
											<div data-testid="tabLink-component">
												<a
													className="mobile-nav-item"
													data-testid="link-component"
													href="https://www.intersport.se/kundservice"
													onClick={onClose}
												>
													<div className="align-center flex flex-row gap-4 font-normal">
														<Icon
															name="question-mark-circled"
															strokeWidth={2}
															className="!font-bold"
														/>{' '}
														Kundservice
													</div>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														fill="none"
														className="icon-container icon-next"
														viewBox="0 0 24 24"
													>
														<path
															stroke="currentColor"
															stroke-width="2"
															d="M9 4l5.55 7.4a1 1 0 010 1.2L9 20"
														></path>
													</svg>
												</a>
											</div>
										</li>
									</ul>
								</nav>
							</div>
							<nav
								className="mobile-footer-nav !w-11/12"
								aria-label="Mobile footer"
							>
								<ul
									className="mobile-footer-list justify-content-evenly flex flex-row"
									data-testid="LinkList-component"
								>
									<li
										className="mobile-footer-link mx-2"
										data-testid="linklist-item"
									>
										<Link
											className="mobile-footer-item pe-0 ps-0"
											data-testid="link-component"
											to="/butiker"
											onClick={onClose}
										>
											<Icon name="map-pin" className="h-6 w-6" />
											<span className="lh-default drawer-link-text">
												Butiker
											</span>
										</Link>
									</li>
									<li
										className="mobile-footer-link mx-2"
										data-testid="linklist-item"
									>
										<Link
											className="mobile-footer-item pe-0 ps-0"
											data-testid="link-component"
											to="/login"
											onClick={onClose}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24"
												height="24"
												fill="none"
												viewBox="0 0 24 24"
											>
												<path
													fill="#fff"
													fill-rule="evenodd"
													d="M12 3a5 5 0 00-3.232 8.815c.174.148.223.278.23.347-.37.288-1.07.611-1.942.95-.305.118-.603.229-.893.336l-.413.154c-.397.148-.795.302-1.092.45A3 3 0 003 16.737V20h18v-3.264a3 3 0 00-1.658-2.683 12.164 12.164 0 00-1.092-.451l-.413-.154c-.29-.107-.588-.218-.893-.336-.872-.339-1.573-.662-1.943-.95.008-.069.057-.2.23-.347A5 5 0 0012 3zM9 8a3 3 0 114.939 2.29c-.485.41-.939 1.066-.939 1.892 0 .399.107.976.584 1.401.655.586 1.75 1.05 2.636 1.393.32.124.645.245.943.355l.386.144c.41.153.71.273.898.367a1 1 0 01.553.894V18H5v-1.264a1 1 0 01.553-.894c.188-.095.489-.214.898-.367l.386-.144c.298-.11.624-.23.943-.354.887-.345 1.98-.808 2.636-1.394.476-.425.584-1.002.584-1.401 0-.826-.454-1.481-.939-1.892A2.991 2.991 0 019 8z"
													clip-rule="evenodd"
												></path>
											</svg>
											<span className="lh-default drawer-link-text">
												Mina sidor
											</span>
										</Link>
									</li>
									<li
										className="mobile-footer-link mx-2"
										data-testid="linklist-item"
									>
										<Link
											className="mobile-footer-item pe-0 ps-0"
											data-testid="link-component"
											to="/kundservice"
											onClick={onClose}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24"
												height="24"
												fill="none"
												viewBox="0 0 24 24"
											>
												<path
													fill="#fff"
													fill-rule="evenodd"
													d="M4 12a8 8 0 1116 0 8 8 0 01-16 0zm8-10C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-.924 10.557c-.08.232-.12.506-.12.822v.32h2.196v-.125c0-.12.022-.242.067-.362.053-.121.111-.219.174-.293l.924-1.212c.188-.25.348-.52.482-.807.134-.288.201-.613.201-.975 0-.483-.085-.905-.255-1.268a2.531 2.531 0 00-.656-.919 2.768 2.768 0 00-.964-.543A3.263 3.263 0 0012 7c-.402 0-.786.065-1.152.195a2.83 2.83 0 00-.964.571 2.612 2.612 0 00-.643.92c-.16.361-.241.78-.241 1.253h2.196c0-.251.063-.46.188-.627.125-.167.33-.25.616-.25.268 0 .469.083.603.25.134.158.2.357.2.599a.924.924 0 01-.107.39c-.071.14-.156.27-.254.39l-.938 1.142a2.206 2.206 0 00-.428.724zm2.103 2.2h-2.25V17h2.25v-2.242z"
													clip-rule="evenodd"
												></path>
											</svg>
											<span className="lh-default drawer-link-text">
												Kundtjänst
											</span>
										</Link>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export { ISEV2Drawer }
